"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import type { PdfBoxDescriptor } from "../pdf-viewer-core-types";
import type { CitationBoundingBox } from "../types";
import { PreviewLoading } from "./preview-loading";
import { TextBasedPreview } from "./text-preview";

export interface PDFPreviewProps {
  fileUrl?: string | undefined;
  filename: string;
  id: string;
  fieldKey?: string | undefined;
  value?: string | undefined;
  citedText: string;
  pageNumber?: number | undefined;
  boundingBox?: CitationBoundingBox | undefined;
  isConflict?: boolean | undefined;
}

// Lazily load the heavy PdfViewerCore so the hover card doesn't pull
// the PDF engine into the main bundle until the user actually hovers.
type PdfViewerCoreComponent = React.ComponentType<{
  fileUrl: string;
  boxes: PdfBoxDescriptor[];
  selectedBoxId?: string | null;
  initialScale?: number;
  initialPage?: number;
}>;

function useLazyPdfViewerCore(): PdfViewerCoreComponent | null {
  const [Component, setComponent] = useState<PdfViewerCoreComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    import("../heavy-pdf").then((mod) => {
      if (!cancelled) {
        setComponent(() => mod.PdfViewerCore as unknown as PdfViewerCoreComponent);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return Component;
}

export function PDFPreview({
  fileUrl,
  filename,
  id,
  value,
  citedText,
  pageNumber,
  boundingBox,
}: PDFPreviewProps) {
  const PdfViewerCore = useLazyPdfViewerCore();

  if (!fileUrl) {
    return (
      <TextBasedPreview
        filename={filename}
        citedText={citedText}
        {...(pageNumber != null ? { pageNumber } : {})}
        fileType="pdf"
      />
    );
  }

  if (!PdfViewerCore) {
    return (
      <div className="h-64 bg-muted/50">
        <PreviewLoading />
      </div>
    );
  }

  const boxes = boundingBox
    ? [
        {
          id,
          bbox: boundingBox,
          label: value ?? null,
          variant: "selected" as const,
        },
      ]
    : [];

  const initialPage = boundingBox?.page ?? pageNumber ?? 1;

  return (
    <div className="relative h-64 bg-muted/50 flex flex-col">
      <PdfViewerCore
        fileUrl={fileUrl}
        boxes={boxes}
        selectedBoxId={boundingBox ? id : null}
        initialPage={initialPage}
        initialScale={0.6}
      />
    </div>
  );
}
